import type { IReportTable } from '~/presentation/reports/common/type';
import type { AnalyticDateFilter } from '~/presentation/ui';

import { useMetricsTimeFilter, getDateQueryVariable, formatDateRange } from '~/modules/analytics';
import { getPreviousColumnName } from '~/presentation/reports/common';
import { useReportControlContext } from '../contexts';
import { useMemo, useCallback } from 'react';
import { dayjsTz } from '~/utils/dayjs';

interface UseTableProps {
  dimensions: string[];
  tableData: IReportTable;
}

export const useReportTable = ({ dimensions, tableData }: UseTableProps) => {
  const { currentDates, previousDates } = useMetricsTimeFilter();

  const { timeDimension } = useReportControlContext();

  const getSummaryTitleByTime = useCallback((dates: AnalyticDateFilter) => {
    const DATE_FORMAT = 'MMM DD, YYYY';
    const dateRange = getDateQueryVariable(dates);
    const startDate = dayjsTz(dateRange.dateGTE);
    const endDate = dayjsTz(dateRange.dateLTE);

    return formatDateRange(startDate, endDate, DATE_FORMAT);
  }, []);

  const isTimeDimensionFirst = dimensions.length > 0 && timeDimension === dimensions[0];

  const currentTitle = useMemo(() => {
    if (isTimeDimensionFirst) {
      return getSummaryTitleByTime(currentDates);
    }
    return 'Summary';
  }, [isTimeDimensionFirst, currentDates, getSummaryTitleByTime]);

  const compareTitle = useMemo(() => {
    if (!isTimeDimensionFirst) return '';

    const firstRow = tableData.rows?.[0];
    const hasCompareData = !!firstRow && getPreviousColumnName(timeDimension) in firstRow;
    if (!hasCompareData) return '';

    return getSummaryTitleByTime(previousDates);
  }, [isTimeDimensionFirst, previousDates, getSummaryTitleByTime, tableData.rows, timeDimension]);

  return {
    summaryTitle: {
      currentTitle,
      compareTitle,
    },
    timeDimension,
  };
};
